import { forwardRef, useMemo, ReactNode } from 'react';
import { Table as MuiTable, TableRowProps, TableRow as MuiTableRow } from '@mui/material';
import { TableProps } from 'react-virtuoso';
import { StyledTableBody, StyledTableHead } from '../StyledTableComponents';

export interface UseVirtualTableComponentsProps {
  rows: any[];
  tableEmptyStateJsx?: ReactNode;
  customTableRow?: React.ComponentType<any>;
}

export interface VirtualTableComponents {
  Table: React.ComponentType<any>;
  TableHead: React.ComponentType<any>;
  TableBody: React.ComponentType<any>;
  TableRow: React.ComponentType<any>;
}

/**
 * Hook that provides shared virtual table components for TableVirtuoso
 * Eliminates duplication between different virtual table implementations
 */
export function useVirtualTableComponents({
  rows,
  tableEmptyStateJsx,
  customTableRow,
}: UseVirtualTableComponentsProps): VirtualTableComponents {

  const components = useMemo(() => {
    const Table = forwardRef<any, TableProps>(({ children, style }, ref) => {
      return (
        <MuiTable
          ref={ref}
          style={
            {
              '--virtuosoPaddingTop': (style?.paddingTop ?? 0) + 'px',
              '--virtuosoPaddingBottom': (style?.paddingBottom ?? 0) + 'px',
            } as any
          }
        >
          {children}
        </MuiTable>
      );
    });

    const TableHead = forwardRef<any, TableProps>(({ children }, ref) => {
      return <StyledTableHead ref={ref}>{children}</StyledTableHead>;
    });

    const TableBody = forwardRef<any, TableProps>((props, ref) => {
      return (
        <StyledTableBody ref={ref} style={{ position: rows.length ? 'relative' : 'initial' }}>
          {!!rows.length ? (
            props.children
          ) : (
            <MuiTableRow
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                '&:hover': { backgroundColor: 'transparent !important' },
              }}
            >
              {tableEmptyStateJsx}
            </MuiTableRow>
          )}
        </StyledTableBody>
      );
    });

    const TableRow = customTableRow || forwardRef<any, TableRowProps>((props, ref) => {
      return <MuiTableRow ref={ref} {...props} />;
    });

    return {
      Table,
      TableHead,
      TableBody,
      TableRow,
    };
  }, [rows.length, tableEmptyStateJsx, customTableRow]);

  return components;
}
